import { Box, Flex } from '@chakra-ui/react';
import { SpinnerLoader } from '@kula-ai/components';
import { File04 } from '@kula-ai/icons-core';
import { colors } from '@/theme/foundations/colors';
import {
  EXTENSION_KIND,
  EXTENSION_LABEL,
  KIND_BG,
  fileExtension,
  type FileKind,
} from './fileTypeMeta';

/**
 * Kaleidoscope `File` component set (Figma P38Omrcw… `4846:11897`): a coloured
 * tile carrying a white page glyph, with the file's kind lettered across it.
 *
 * kula ships a `FileTypeIcon` beside its `AttachmentPreview`, but it is not
 * exported from `@kula-ai/components` and its three colours are hardcoded hexes
 * (#EF4444 / #7C6FCD / #0D9488) that match none of the design-system values
 * below. Exporting it and moving it onto tokens looks like it belongs upstream.
 */

export interface FileTypeTileProps {
  filename?: string;
  mimeType?: string;
  /** Tile edge in px. The design system draws it at 44; chips use 32. */
  size?: number;
  /**
   * The file is still being uploaded, so its kind is not settled — the tile
   * holds a loader in place of the glyph rather than briefly lettering a kind
   * that may not be the one that lands.
   */
  loading?: boolean;
}

export const FileTypeTile = ({
  filename,
  mimeType,
  size = 32,
  loading = false,
}: FileTypeTileProps) => {
  // Ratios read off Figma 2673:4691 (the 32px chip tile) and cross-checked
  // against the 44px design-system variant, so both sizes stay on spec:
  // glyph 12x15 @32 / 16x20 @44, label 4.36px @32 / 6px @44, radius 4.36 @32.
  const glyphHeight = size * 0.47;
  const glyphWidth = size * 0.375;
  const labelSize = size * 0.136;
  const tileRadius = size * 0.136;

  const extension = fileExtension(filename, mimeType);
  const kind: FileKind = EXTENSION_KIND[extension] ?? 'other';
  const label = EXTENSION_LABEL[extension];

  if (loading) {
    return (
      <Flex
        align="center"
        justify="center"
        flexShrink={0}
        w={`${size}px`}
        h={`${size}px`}
        borderRadius="6px"
        bg="secondary-50"
        data-test-id="file-type-tile-loading"
      >
        <SpinnerLoader size="sm" />
      </Flex>
    );
  }

  // Unknown kinds get the plain outline glyph on a grey tile — no lettering,
  // since there is no three-letter kind the design system would show.
  if (kind === 'other') {
    return (
      <Flex
        align="center"
        justify="center"
        flexShrink={0}
        w={`${size}px`}
        h={`${size}px`}
        borderRadius={`${tileRadius}px`}
        bg={KIND_BG.other}
        data-test-id="file-type-tile"
        data-kind="other"
      >
        {/* icons-core renders a bare <svg>, so a Chakra token means nothing to
            it — the resolved value comes from the same palette. */}
        <File04
          size={Math.round(size * 0.55)}
          color={colors['secondary-400']}
        />
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      justify="center"
      flexShrink={0}
      w={`${size}px`}
      h={`${size}px`}
      borderRadius={`${tileRadius}px`}
      bg={KIND_BG[kind]}
      px={`${size * 0.09}px`}
      py={`${size * 0.045}px`}
      data-test-id="file-type-tile"
      data-kind={kind}
    >
      <Box position="relative" w={`${glyphWidth}px`} h={`${glyphHeight}px`}>
        {/* A filled white page with a folded corner. Drawn inline rather than
            taken from icons-core, which ships only the outline File04 — the
            design system's tile needs a solid fill for the lettering to sit
            on. */}
        <svg
          width={glyphWidth}
          height={glyphHeight}
          viewBox="0 0 16 20"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M2 1.5A1.5 1.5 0 0 1 3.5 0H10l6 6v12.5A1.5 1.5 0 0 1 14.5 20h-11A1.5 1.5 0 0 1 2 18.5v-17Z"
            fill="white"
          />
          <path d="M10 0l6 6h-4.5A1.5 1.5 0 0 1 10 4.5V0Z" fill="white" />
        </svg>
        {label && (
          <Box
            position="absolute"
            // Figma: the lettering is 12x7 inside a 12x15 page — it sits on
            // the lower third, not centred, and at a size that leaves the page
            // edges visible around it.
            bottom="8%"
            // The page glyph's fold sits on its right, so its visual centre is
            // left of the box's centre — the lettering needs nudging back
            // toward the fold to look centred on the page rather than on the
            // bounding box.
            left="2px"
            right={0}
            textAlign="center"
            fontSize={`${labelSize}px`}
            fontWeight="bold"
            lineHeight="1"
            color={KIND_BG[kind]}
            data-test-id="file-type-tile-label"
          >
            {label}
          </Box>
        )}
      </Box>
    </Flex>
  );
};
